import { useId, useState } from 'react';
import { type Control, useWatch } from 'react-hook-form';
import { z } from 'zod';

import { Field, FieldError, FieldLabel } from '@/components/ui/field';
import { Input } from '@/components/ui/input';
// Deep import: the leads barrel pulls in route components.
import { useLead } from '@/features/leads/api/get-lead';
import { MOBILE_INPUT_CLASS } from '@/features/mobile-ui';
import { useVirtualMeetingsEnabled } from '@/features/services-dashboard/service-form/use-virtual-meetings-enabled';
import { cn } from '@/lib/utils';

import type { AppointmentFieldVariant } from './appointment-create-fields';
import type {
  AppointmentCreateFormData,
  AppointmentCreateService,
} from './appointment-create-form';

const emailSchema = z.string().trim().email();

/**
 * An online (Google Meet) service is confirmed by email with the Meet link, so
 * a client with no email on file is asked for one. Like rooms and packages it
 * rides alongside the form: every other booking sends an unchanged body.
 */
export interface AppointmentClientEmailSelection {
  /** Online service, and the chosen client has no email on file. */
  needed: boolean;
  email: string;
  setEmail: (email: string) => void;
  error: string | null;
  /** False (and shows the error) when an email is needed but not valid. */
  validate: () => boolean;
  /** Sent with the booking; undefined when nothing was asked. */
  clientEmail: string | undefined;
}

export function useAppointmentClientEmail(
  control: Control<AppointmentCreateFormData>,
  services: readonly AppointmentCreateService[],
  /** Services added after the first, when the booking is a visit. */
  additionalServiceIds: readonly string[] = []
): AppointmentClientEmailSelection {
  const virtualMeetingsEnabled = useVirtualMeetingsEnabled();
  const leadId = useWatch({ control, name: 'leadId' }) ?? '';
  const serviceId = useWatch({ control, name: 'serviceId' });
  // Only when every booked service is a video call: a visit with anything in
  // clinic is not an online booking.
  const bookedIds = [serviceId, ...additionalServiceIds].filter(Boolean);
  const online =
    virtualMeetingsEnabled &&
    bookedIds.length > 0 &&
    bookedIds.every(
      (id) => services.find((service) => service.id === id)?.virtualMeeting
    );
  const { lead } = useLead({
    leadId,
    queryConfig: { enabled: online && Boolean(leadId) },
  });
  const needed = online && Boolean(lead) && !lead?.email?.trim();

  const [email, setEmail] = useState('');
  const [error, setError] = useState<string | null>(null);
  // A different client starts from an empty box.
  const [emailFor, setEmailFor] = useState(leadId);
  if (emailFor !== leadId) {
    setEmailFor(leadId);
    setEmail('');
    setError(null);
  }

  return {
    needed,
    email,
    setEmail: (value) => {
      setEmail(value);
      setError(null);
    },
    error,
    validate: () => {
      if (!needed || emailSchema.safeParse(email).success) return true;
      setError("Enter the client's email to send them the Google Meet link");
      return false;
    },
    clientEmail: needed ? email.trim() : undefined,
  };
}

/** "Client email", shown only when an online-only booking has nowhere to send the link. */
export function AppointmentClientEmailField({
  selection,
  variant = 'desktop',
}: {
  selection: AppointmentClientEmailSelection;
  variant?: AppointmentFieldVariant;
}) {
  const id = useId();
  if (!selection.needed) return null;
  return (
    <Field data-invalid={Boolean(selection.error)}>
      <FieldLabel htmlFor={id}>Client email</FieldLabel>
      <Input
        id={id}
        type="email"
        inputMode="email"
        autoComplete="off"
        placeholder="name@example.com"
        value={selection.email}
        onChange={(event) => selection.setEmail(event.target.value)}
        aria-invalid={Boolean(selection.error)}
        className={cn(variant === 'mobile' && MOBILE_INPUT_CLASS)}
      />
      {selection.error && <FieldError>{selection.error}</FieldError>}
    </Field>
  );
}
